// Board layout по образцу прототипа Claude Design.
// Полное меню колонки (rename/WIP/тип/удалить/добавить рядом) и lane (rename/цвет/удалить).
// DnD карточек через @dnd-kit с PointerSensor (distance: 5) — клик не срывается в drag.
// Lexorank-style sortOrder для карточек.

import { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
  type DragMoveEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Noop strategy для variable-width grid (колонки). Built-in strategies
// (horizontal/rect) предполагают одинаковую ширину item'ов и ломаются
// на нашем grid с `gridColumn: span N`. Reorder делается через
// onDragOver + sortable transform на header + transform на cells. Соседи
// двигаются мгновенно через swap sortOrder. См. dnd-kit #44, #93, #720.
const noopStrategy = () => null;
import {
  ChevronDown,
  Plus,
  MoreHorizontal,
  Maximize2,
  Minimize2,
  CircleDot,
  Circle,
  Check,
  GripVertical,
  Edit3,
  Trash2,
  Gauge,
  Archive,
  Edit2,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';
import { TaskCard } from './TaskCard';
import { TaskModal } from './TaskModal';
import type { BoardRow, ColumnRow, LaneRow, CardRow, ColumnKind } from '../types';

// Portal-обёртка для popover-меню. Раньше меню рендерились через `absolute`
// внутри ячейки/колонки — обрезались `overflow: hidden` родителей (доска,
// канвас). Сейчас меню рендерится в document.body, позиция считается
// от bounding rect якоря. Закрытие — клик вне области.
function PopoverPortal({
  anchorRef,
  open,
  onClose,
  align = 'right',
  children,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  align?: 'left' | 'right';
  children: React.ReactNode;
}) {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Позиция считается ПЕРЕД paint, чтобы меню не «прыгало» в кадре открытия.
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPos(null);
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    const top = rect.bottom + 4;
    const left = align === 'right' ? rect.right : rect.left;
    setPos({ top, left });
  }, [open, anchorRef, align]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const popover = popoverRef.current;
      if (popover && popover.contains(e.target as Node)) return;
      if (anchorRef.current && anchorRef.current.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onClose, anchorRef]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        top: pos.top,
        left: align === 'right' ? undefined : pos.left,
        right: align === 'right' ? Math.max(0, window.innerWidth - pos.left) : undefined,
        zIndex: 1000,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}

const NORMAL_W = 280;
const WIDE_W = 520;

const COLUMN_KIND: Record<ColumnKind, { label: string; accent: string; icon: 'circle' | 'circle-dot' | 'check' }> = {
  queue: { label: 'Очередь', accent: '#9aa0a6', icon: 'circle' },
  'in-progress': { label: 'В работе', accent: '#3b82f6', icon: 'circle-dot' },
  review: { label: 'Ревью', accent: '#a855f7', icon: 'circle-dot' },
  done: { label: 'Готово', accent: '#10b981', icon: 'check' },
};

const LANE_PALETTE = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#9aa0a6'];

interface BoardProps {
  board: BoardRow;
  onDelete?: () => void;
  isDragging?: boolean;
}

export function Board({ board, onDelete, isDragging: isBoardDragging }: BoardProps) {
  const fullQuery = api.boards.getFull.useQuery({ boardId: board.id });
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(board.collapsed);
  // Drag state для column/lane reorder. Используется для:
  //   1. ColumnCell получает delta → применяет transform → cells движутся
  //      синхронно с header (решает «двигается только шапка»).
  //   2. handleDragEnd использует activeId для синхронизации с сервером.
  // DragOverlay не используем — он рендерится в body через portal и ломается
  // когда canvas внутри `transform: scale(zoom)` (overlay-coords ≠ canvas-coords).
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
  const [columnDragDelta, setColumnDragDelta] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const utils = api.useUtils();
  // Optimistic update для DnD карточек: мгновенно перерисовать в новой ячейке (#C4)
  const moveMutation = api.cards.move.useMutation({
    onMutate: async (input) => {
      await utils.boards.getFull.cancel({ boardId: board.id });
      const prev = utils.boards.getFull.getData({ boardId: board.id });
      utils.boards.getFull.setData({ boardId: board.id }, (old) => {
        if (!old) return old;
        return {
          ...old,
          cards: old.cards.map((c) =>
            c.id === input.cardId
              ? { ...c, columnId: input.toColumnId, laneId: input.toLaneId }
              : c,
          ),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.boards.getFull.setData({ boardId: board.id }, ctx.prev);
    },
    onSettled: () => utils.boards.getFull.invalidate({ boardId: board.id }),
  });
  const toggleCollapseMutation = api.boards.toggleCollapsed.useMutation();
  const deleteMutation = api.boards.delete.useMutation({
    onSuccess: () => onDelete?.(),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // ВСЕ hooks должны вызываться ДО early return (Rules of Hooks).
  // useMemo читает данные через optional chain, чтобы безопасно работать пока fullQuery не загружен.
  const columns = useMemo(() => {
    const raw = (fullQuery.data?.columns ?? []) as ColumnRow[];
    return raw.filter((c) => !c.parentColumnId).sort((a, b) => a.sortOrder - b.sortOrder);
  }, [fullQuery.data]);

  // Под-колонки: row сгруппированных детей под группирующими колонками
  const subColumnsByParent = useMemo(() => {
    const raw = (fullQuery.data?.columns ?? []) as ColumnRow[];
    const map = new Map<string, ColumnRow[]>();
    for (const c of raw) {
      if (c.parentColumnId) {
        if (!map.has(c.parentColumnId)) map.set(c.parentColumnId, []);
        map.get(c.parentColumnId)!.push(c);
      }
    }
    for (const arr of map.values()) arr.sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [fullQuery.data]);

  const lanes = useMemo(() => {
    const raw = (fullQuery.data?.lanes ?? []) as LaneRow[];
    return [...raw].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [fullQuery.data]);

  const cards = (fullQuery.data?.cards ?? []) as CardRow[];

  // Для column/lane reorder — onSettled вместо onSuccess. Optimistic update
  // делается прямо в handleDragEnd (через setData с пересчётом sortOrder),
  // чтобы колонка не возвращалась на старое место в момент завершения drag,
  // ожидая server response. Если сервер вернёт ошибку — onError откатит.
  const moveColumnMutation = api.columns.move.useMutation({
    onSettled: () => utils.boards.getFull.invalidate({ boardId: board.id }),
  });
  const moveLaneMutation = api.lanes.move.useMutation({
    onSettled: () => utils.boards.getFull.invalidate({ boardId: board.id }),
  });

  const handleDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as { type?: string } | undefined;
    if (data?.type === 'column') {
      setActiveColumnId(e.active.id as string);
      setColumnDragDelta({ x: 0, y: 0 });
    }
    // Lane drag не нуждается в state — sortable.transform применяется к
    // полно-ширинному lane header'у напрямую, soседи не нужно двигать.
  };

  // onDragMove обновляет delta — ColumnCell этой колонки получает её и
  // применяет transform синхронно с header. Без этого видно что движется
  // только header колонки, а cells остаются на месте.
  const handleDragMove = (e: DragMoveEvent) => {
    if (activeColumnId && e.active.id === activeColumnId) {
      setColumnDragDelta({ x: e.delta.x, y: e.delta.y });
    }
  };

  // onDragOver — immediate reorder через swap sortOrder в кэше.
  // Это даёт «соседи расступаются под местом куда несёшь» эффект без
  // sortable strategy (которая ломается на variable-width grid).
  const handleDragOver = (e: DragOverEvent) => {
    const activeData = e.active.data.current as { type?: string } | undefined;
    const overData = e.over?.data.current as { type?: string } | undefined;
    if (!activeData || !overData || !e.over || e.active.id === e.over.id) return;

    if (activeData.type === 'column' && overData.type === 'column') {
      const oldIdx = columns.findIndex((c) => c.id === e.active.id);
      const newIdx = columns.findIndex((c) => c.id === e.over!.id);
      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
      // Swap sortOrder активной с целевой — простой и стабильный
      const targetSort = columns[newIdx].sortOrder;
      const activeSort = columns[oldIdx].sortOrder;
      utils.boards.getFull.setData({ boardId: board.id }, (old) => {
        if (!old) return old;
        return {
          ...old,
          columns: old.columns.map((c) => {
            if (c.id === e.active.id) return { ...c, sortOrder: targetSort };
            if (c.id === e.over!.id) return { ...c, sortOrder: activeSort };
            return c;
          }),
        };
      });
      return;
    }

    if (activeData.type === 'lane' && overData.type === 'lane') {
      const oldIdx = lanes.findIndex((l) => l.id === e.active.id);
      const newIdx = lanes.findIndex((l) => l.id === e.over!.id);
      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
      const targetSort = lanes[newIdx].sortOrder;
      const activeSort = lanes[oldIdx].sortOrder;
      utils.boards.getFull.setData({ boardId: board.id }, (old) => {
        if (!old) return old;
        return {
          ...old,
          lanes: old.lanes.map((l) => {
            if (l.id === e.active.id) return { ...l, sortOrder: targetSort };
            if (l.id === e.over!.id) return { ...l, sortOrder: activeSort };
            return l;
          }),
        };
      });
    }
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveColumnId(null);
    setColumnDragDelta({ x: 0, y: 0 });
    const activeData = e.active.data.current as
      | { type?: string; card?: CardRow; column?: ColumnRow; lane?: LaneRow }
      | undefined;
    const overData = e.over?.data.current as
      | { type?: string; columnId?: string; laneId?: string; column?: ColumnRow; lane?: LaneRow }
      | undefined;
    if (!activeData || !overData || !e.over) return;

    // Drag карточки между ячейками
    if (activeData.type === 'card' && overData.type === 'cell' && activeData.card) {
      if (
        activeData.card.columnId === overData.columnId &&
        activeData.card.laneId === overData.laneId
      ) {
        return;
      }
      moveMutation.mutate({
        cardId: activeData.card.id,
        toColumnId: overData.columnId!,
        toLaneId: overData.laneId!,
        beforeCardId: null,
        afterCardId: null,
      });
      return;
    }

    // Column reorder: onDragOver уже применил optimistic-swap.
    // Здесь только синхронизируем с сервером — берём текущий порядок
    // (после onDragOver) и шлём before/after.
    if (activeData.type === 'column' && overData.type === 'column') {
      const idx = columns.findIndex((c) => c.id === e.active.id);
      if (idx === -1) return;
      const prev = columns[idx - 1];
      const next = columns[idx + 1];
      moveColumnMutation.mutate({
        id: e.active.id as string,
        beforeColumnId: next?.id ?? null,
        afterColumnId: prev?.id ?? null,
      });
      return;
    }

    if (activeData.type === 'lane' && overData.type === 'lane') {
      const idx = lanes.findIndex((l) => l.id === e.active.id);
      if (idx === -1) return;
      const prev = lanes[idx - 1];
      const next = lanes[idx + 1];
      moveLaneMutation.mutate({
        id: e.active.id as string,
        beforeLaneId: next?.id ?? null,
        afterLaneId: prev?.id ?? null,
      });
    }
  };

  // Early return ПОСЛЕ всех hooks — это легально, hooks порядок не меняется
  if (!fullQuery.data) {
    return (
      <div className="rounded-lg shadow-board-rest bg-surface-light dark:bg-surface-dark p-3 text-xs text-ink-faint_l">
        Загрузка доски «{board.title}»…
      </div>
    );
  }

  // gridTemplate учитывает под-колонки: группа с N детьми занимает N × NORMAL_W
  const gridTemplate = columns
    .map((c) => {
      const subs = subColumnsByParent.get(c.id) ?? [];
      if (subs.length > 0) {
        return subs.map((s) => `${s.isWide ? WIDE_W : NORMAL_W}px`).join(' ');
      }
      return `${c.isWide ? WIDE_W : NORMAL_W}px`;
    })
    .join(' ');

  // Список «leaf» колонок — реальные cells на доске (под-колонки если есть, иначе сами).
  // По ним делается gridTemplateColumns + per-cell drop zones.
  const leafColumns: ColumnRow[] = columns.flatMap((c) => {
    const subs = subColumnsByParent.get(c.id) ?? [];
    return subs.length > 0 ? subs : [c];
  });

  const handleToggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    toggleCollapseMutation.mutate({ id: board.id, collapsed: next });
  };

  return (
    <>
      <div
        className={clsx(
          'group/board inline-block rounded-lg overflow-hidden bg-surface-light dark:bg-surface-dark',
          isBoardDragging ? 'shadow-board-drag' : 'shadow-board-rest hover:shadow-board-hover',
          'transition-shadow',
        )}
        style={{
          // Плавная collapse через grid-template-rows из прототипа.
          // Не используем здесь — collapse реализован через {!collapsed && ...} ниже.
        }}
      >
        <BoardHeader
          board={board}
          totalCards={cards.length}
          collapsed={collapsed}
          onToggleCollapse={handleToggleCollapse}
          onDelete={() => {
            if (window.confirm(`Удалить доску «${board.title}»?`)) {
              deleteMutation.mutate({ id: board.id });
            }
          }}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateRows: collapsed ? '0fr' : '1fr',
            transition: 'grid-template-rows 280ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          }}
        >
          <div style={{ overflow: 'hidden', minHeight: 0 }}>
        {(
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            {/* Top row — group headers. SortableContext с noop strategy
                (см. выше) — sortable transform НЕ применяется ни к
                draggable, ни к соседям. Reorder через onDragOver + DragOverlay. */}
            <SortableContext
              items={columns.map((c) => c.id)}
              strategy={noopStrategy}
            >
              <div
                className="grid border-t border-line-light dark:border-line-dark"
                style={{
                  gridTemplateColumns: gridTemplate,
                  transition: 'grid-template-columns var(--dur-med) var(--ease-out)',
                }}
              >
                {columns.map((col, i) => {
                  const subs = subColumnsByParent.get(col.id) ?? [];
                  const span = subs.length > 0 ? subs.length : 1;
                  const colCount = subs.length > 0
                    ? subs.reduce((sum, s) => sum + cards.filter((c) => c.columnId === s.id).length, 0)
                    : cards.filter((c) => c.columnId === col.id).length;
                  return (
                    <DraggableColumnHeader
                      key={col.id}
                      column={col}
                      span={span}
                      boardId={board.id}
                      count={colCount}
                      prevColumn={i > 0 ? columns[i - 1] : null}
                      nextColumn={i < columns.length - 1 ? columns[i + 1] : null}
                      isGroup={subs.length > 0}
                      isFirst={i === 0}
                    />
                  );
                })}
              </div>
            </SortableContext>

            {/* Если есть группы — второй ряд для sub-column headers */}
            {columns.some((c) => (subColumnsByParent.get(c.id) ?? []).length > 0) && (
              <div
                className="grid border-t border-line-light dark:border-line-dark text-[11px]"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                {(() => {
                  // Плоский список ячеек top-row подколонок: каждая со своим
                  // border-l кроме самой первой. Границы доходят между всеми
                  // sub-cells единым потоком.
                  let globalIdx = 0;
                  return columns.flatMap((col) => {
                    const subs = subColumnsByParent.get(col.id) ?? [];
                    if (subs.length === 0) {
                      const idx = globalIdx++;
                      return (
                        <div
                          key={`empty-${col.id}`}
                          className={clsx(
                            'h-6',
                            idx > 0 && 'border-l border-line-light dark:border-line-dark',
                          )}
                        />
                      );
                    }
                    return subs.map((sub) => {
                      const idx = globalIdx++;
                      return (
                        <div
                          key={sub.id}
                          className={clsx(
                            'px-2 h-6 flex items-center gap-1.5',
                            idx > 0 && 'border-l border-line-light dark:border-line-dark',
                          )}
                        >
                          <span className="w-1 h-1 rounded-full bg-ink-faint_l dark:bg-ink-faint_d" />
                          <span className="text-ink-muted_l dark:text-ink-muted_d truncate flex-1">{sub.title}</span>
                          <span className="text-ink-faint_l dark:text-ink-faint_d tabular-nums">
                            {cards.filter((c) => c.columnId === sub.id).length}
                          </span>
                        </div>
                      );
                    });
                  });
                })()}
              </div>
            )}

            {lanes.length === 0 ? (
              <div className="p-4 text-xs text-ink-faint_l text-center">
                Нет дорожек. Добавь через меню «⋯» в шапке доски.
              </div>
            ) : (
              <SortableContext
                items={lanes.map((l) => l.id)}
                strategy={verticalListSortingStrategy}
              >
                {lanes.map((lane, idx) => (
                  <LaneSection
                    key={lane.id}
                    board={board}
                    lane={lane}
                    columns={leafColumns}
                    cards={cards.filter((c) => c.laneId === lane.id)}
                    showLaneHeader={lanes.length > 1}
                    gridTemplate={gridTemplate}
                    onOpenCard={setOpenCardId}
                    prevLane={idx > 0 ? lanes[idx - 1] : null}
                    nextLane={idx < lanes.length - 1 ? lanes[idx + 1] : null}
                    activeColumnId={activeColumnId}
                    columnDragDelta={columnDragDelta}
                  />
                ))}
              </SortableContext>
            )}
          </DndContext>
        )}
          </div>
        </div>
      </div>

      {openCardId && <TaskModal cardId={openCardId} onClose={() => setOpenCardId(null)} boardCards={cards} />}
    </>
  );
}

function BoardHeader({
  board,
  totalCards,
  collapsed,
  onToggleCollapse,
  onDelete,
}: {
  board: BoardRow;
  totalCards: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onDelete: () => void;
}) {
  const utils = api.useUtils();
  const renameMutation = api.boards.rename.useMutation({
    onSuccess: () => {
      utils.boards.getFull.invalidate({ boardId: board.id });
      utils.spaces.getWithBoards.invalidate();
    },
  });
  const createLaneMutation = api.lanes.create.useMutation({
    onSuccess: () => utils.boards.getFull.invalidate({ boardId: board.id }),
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(board.title);
  const [menuOpen, setMenuOpen] = useState(false);

  const submit = () => {
    const t = draft.trim();
    if (t && t !== board.title) renameMutation.mutate({ id: board.id, title: t });
    setEditing(false);
  };

  const stopMouse = (e: React.MouseEvent) => e.stopPropagation();

  // Layout по прототипу Claude Design: thin-header h-10, элементы плотно
  // друг к другу, кнопки добавления и меню — прямо в шапке (не в выпадающем).
  // Drag-handle = весь header (cursor-grab), кроме интерактивных элементов
  // которые останавливают propagation.
  return (
    <header
      className="flex items-center gap-1.5 px-2 h-7 bg-surface-light dark:bg-surface-dark cursor-grab active:cursor-grabbing select-none"
      data-board-header="1"
    >
      {/* Порядок как у дорожек: сначала grip (переместить), потом chevron
          (свернуть). Раньше chevron был первым — пользователь попросил
          унификацию с lane header. */}
      <span
        className="inline-flex items-center justify-center w-3 text-ink-faint_l dark:text-ink-faint_d opacity-60"
        title="Перетащите доску"
      >
        <GripVertical size={11} />
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
        onMouseDown={stopMouse}
        className="inline-flex items-center justify-center w-4 h-4 shrink-0 text-ink-faint_l dark:text-ink-faint_d hover:text-ink-light dark:hover:text-ink-dark"
        aria-label={collapsed ? 'Развернуть' : 'Свернуть'}
      >
        <ChevronDown
          size={12}
          strokeWidth={2}
          style={{
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
            transition: 'transform 200ms',
          }}
        />
      </button>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={submit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            if (e.key === 'Escape') { setDraft(board.title); setEditing(false); }
          }}
          onMouseDown={stopMouse}
          onClick={stopMouse}
          className="text-[13px] font-semibold flex-1 truncate bg-transparent border-b border-accent outline-none min-w-0"
        />
      ) : (
        <h2
          className="text-[13px] font-semibold flex-1 truncate text-ink-light dark:text-ink-dark min-w-0"
          onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
        >
          {board.title}
        </h2>
      )}
      <span className="text-[11px] tabular-nums text-ink-faint_l dark:text-ink-faint_d">
        {totalCards}
      </span>
      {/* Справа — только меню «⋯». Кнопка «+ Колонка» убрана: колонки
          добавляются из ⋯-меню колонки (Добавить слева/справа). */}
      <BoardHeaderMenuButton
        onRename={() => setEditing(true)}
        onAddLane={() => {
          const title = window.prompt('Название дорожки:', 'Новая дорожка');
          if (title?.trim()) createLaneMutation.mutate({ boardId: board.id, title: title.trim() });
        }}
        onDelete={onDelete}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
    </header>
  );
}

function BoardHeaderMenuButton({
  onRename,
  onAddLane,
  onDelete,
  menuOpen,
  setMenuOpen,
}: {
  onRename: () => void;
  onAddLane: () => void;
  onDelete: () => void;
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
}) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen(!menuOpen);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center w-6 h-6 rounded text-ink-faint_l dark:text-ink-faint_d hover:bg-surface2-light dark:hover:bg-surface2-dark hover:text-ink-light dark:hover:text-ink-dark"
        title="Меню доски"
        aria-label="Меню доски"
      >
        <MoreHorizontal size={14} />
      </button>
      <PopoverPortal anchorRef={anchorRef} open={menuOpen} onClose={() => setMenuOpen(false)}>
        <BoardMenu
          onClose={() => setMenuOpen(false)}
          onRename={onRename}
          onAddLane={onAddLane}
          onDelete={onDelete}
        />
      </PopoverPortal>
    </>
  );
}

function BoardMenu({
  onClose,
  onRename,
  onAddLane,
  onDelete,
}: {
  onClose: () => void;
  onRename: () => void;
  onAddLane: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="min-w-[180px] bg-surface-light dark:bg-surface-dark border border-line-light dark:border-line-dark rounded-lg shadow-pop p-1 animate-pop">
      <MenuItem icon={Edit3} label="Переименовать" onClick={() => { onRename(); onClose(); }} />
      <MenuItem icon={Plus} label="Добавить дорожку" onClick={() => { onAddLane(); onClose(); }} />
      <Sep />
      <MenuItem icon={Trash2} label="Удалить доску" danger onClick={() => { onDelete(); onClose(); }} />
    </div>
  );
}

function DraggableColumnHeader({
  column,
  span,
  boardId,
  count,
  prevColumn,
  nextColumn,
  isGroup,
  isFirst,
}: {
  column: ColumnRow;
  span: number;
  boardId: string;
  count: number;
  prevColumn: ColumnRow | null;
  nextColumn: ColumnRow | null;
  isGroup: boolean;
  isFirst: boolean;
}) {
  // useSortable с transform на header. Transform применяется к header при
  // drag — header следует за курсором. Cells этой колонки получают тот же
  // delta через ColumnCell props (isDragging+columnDragDelta из Board state).
  const sortable = useSortable({
    id: column.id,
    data: { type: 'column', column },
    transition: { duration: 200, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
  });

  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        gridColumn: `span ${span}`,
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        zIndex: sortable.isDragging ? 100 : undefined,
      }}
      className={clsx(
        'relative',
        // Border-l скрываем у dragged column — иначе он движется вместе с
        // колонкой и оставляет visual «двойную» полоску. У статичных колонок
        // остаётся как раньше.
        !isFirst && !sortable.isDragging && 'border-l border-line-light dark:border-line-dark',
        isGroup && 'bg-surface2-light/40 dark:bg-surface2-dark/30',
      )}
    >
      <ColumnHeader
        column={column}
        boardId={boardId}
        count={count}
        prevColumn={prevColumn}
        nextColumn={nextColumn}
        dragHandleProps={{ ...sortable.attributes, ...sortable.listeners }}
      />
    </div>
  );
}

function ColumnHeader({
  column,
  boardId,
  count,
  prevColumn,
  nextColumn,
  dragHandleProps,
}: {
  column: ColumnRow;
  boardId: string;
  count: number;
  prevColumn: ColumnRow | null;
  nextColumn: ColumnRow | null;
  isGroup?: boolean;
  dragHandleProps?: Record<string, unknown>;
}) {
  const kind = COLUMN_KIND[column.kind] ?? COLUMN_KIND.queue;
  const wipExceeded = column.wipLimit != null && count > column.wipLimit;
  const KindIcon = kind.icon === 'check' ? Check : kind.icon === 'circle-dot' ? CircleDot : Circle;

  const utils = api.useUtils();
  const updateMutation = api.columns.update.useMutation({
    onSuccess: () => utils.boards.getFull.invalidate({ boardId }),
  });
  const deleteMutation = api.columns.delete.useMutation({
    onSuccess: () => utils.boards.getFull.invalidate({ boardId }),
  });
  const clearMutation = api.columns.clear.useMutation({
    onSuccess: () => utils.boards.getFull.invalidate({ boardId }),
  });
  const moveMutation = api.columns.move.useMutation({
    onSuccess: () => utils.boards.getFull.invalidate({ boardId }),
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(column.title);
  const [menuOpen, setMenuOpen] = useState(false);

  const submitRename = () => {
    const t = draft.trim();
    if (t && t !== column.title) updateMutation.mutate({ id: column.id, title: t });
    setEditing(false);
  };

  return (
    <div
      className="flex items-center gap-1.5 px-2 h-7 cursor-grab active:cursor-grabbing"
      {...(dragHandleProps ?? {})}
    >
      <span
        className="inline-flex items-center justify-center w-3 shrink-0 text-ink-faint_l dark:text-ink-faint_d opacity-50"
        title="Перетащите колонку"
      >
        <GripVertical size={11} />
      </span>
      <span className="inline-flex items-center justify-center shrink-0" style={{ color: kind.accent }}>
        <KindIcon size={11} strokeWidth={2.2} />
      </span>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={submitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitRename();
            }
            if (e.key === 'Escape') {
              setDraft(column.title);
              setEditing(false);
            }
          }}
          className="text-[11.5px] font-semibold uppercase tracking-wider truncate flex-1 leading-none bg-transparent border-b border-accent outline-none"
          style={{ letterSpacing: '0.04em' }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        />
      ) : (
        <h2
          className="text-[11.5px] font-semibold uppercase tracking-wider truncate flex-1 leading-none text-ink-muted_l dark:text-ink-muted_d cursor-text"
          style={{ letterSpacing: '0.04em' }}
          onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
        >
          {column.title}
        </h2>
      )}
      <span
        className={clsx(
          'inline-flex items-center text-[11px] font-medium tabular-nums leading-none',
          wipExceeded ? 'text-red-500 dark:text-red-400' : 'text-ink-faint_l dark:text-ink-faint_d',
        )}
        title={column.wipLimit != null ? `WIP-лимит: ${column.wipLimit}` : undefined}
      >
        {count}
        {column.wipLimit != null ? `/${column.wipLimit}` : ''}
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); updateMutation.mutate({ id: column.id, isWide: !column.isWide }); }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center w-4 h-4 rounded text-ink-faint_l dark:text-ink-faint_d hover:bg-surface3-light dark:hover:bg-surface3-dark transition"
        title={column.isWide ? 'Обычная ширина' : 'Расширить колонку'}
      >
        {column.isWide ? <Minimize2 size={11} /> : <Maximize2 size={11} />}
      </button>
      <ColumnMenuButton
        column={column}
        boardId={boardId}
        canMoveLeft={!!prevColumn}
        canMoveRight={!!nextColumn}
        onRename={() => setEditing(true)}
        onSetKind={(k) => updateMutation.mutate({ id: column.id, kind: k })}
        onSetWip={(w) => updateMutation.mutate({ id: column.id, wipLimit: w })}
        onMoveLeft={() => {
          if (!prevColumn) return;
          moveMutation.mutate({
            id: column.id,
            beforeColumnId: null,
            afterColumnId: prevColumn.id,
          });
        }}
        onMoveRight={() => {
          if (!nextColumn) return;
          moveMutation.mutate({
            id: column.id,
            beforeColumnId: nextColumn.id,
            afterColumnId: null,
          });
        }}
        onClear={() => {
          if (window.confirm(`Удалить все карточки в «${column.title}»?`)) {
            clearMutation.mutate({ id: column.id });
          }
        }}
        onDelete={() => {
          if (window.confirm(`Удалить колонку «${column.title}»? Карточки будут перенесены в первую другую колонку.`)) {
            deleteMutation.mutate({ id: column.id });
          }
        }}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />
    </div>
  );
}

function ColumnMenuButton(props: {
  column: ColumnRow;
  boardId: string;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onRename: () => void;
  onSetKind: (k: ColumnKind) => void;
  onSetWip: (w: number | null) => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onClear: () => void;
  onDelete: () => void;
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
}) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const { menuOpen, setMenuOpen, ...rest } = props;
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center w-4 h-4 rounded text-ink-faint_l dark:text-ink-faint_d hover:bg-surface3-light dark:hover:bg-surface3-dark transition"
        title="Меню"
      >
        <MoreHorizontal size={11} />
      </button>
      <PopoverPortal anchorRef={anchorRef} open={menuOpen} onClose={() => setMenuOpen(false)}>
        <ColumnMenu {...rest} onClose={() => setMenuOpen(false)} />
      </PopoverPortal>
    </>
  );
}

function ColumnMenu({
  column,
  boardId,
  canMoveLeft,
  canMoveRight,
  onClose,
  onRename,
  onSetKind,
  onSetWip,
  onMoveLeft,
  onMoveRight,
  onClear,
  onDelete,
}: {
  column: ColumnRow;
  boardId: string;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onClose: () => void;
  onRename: () => void;
  onSetKind: (k: ColumnKind) => void;
  onSetWip: (w: number | null) => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onClear: () => void;
  onDelete: () => void;
}) {
  const utils = api.useUtils();
  const createMutation = api.columns.create.useMutation({
    onSuccess: () => utils.boards.getFull.invalidate({ boardId }),
  });

  const handleAddNeighbor = (after: boolean) => {
    const title = window.prompt('Название колонки:', 'Новая колонка');
    if (!title?.trim()) return;
    createMutation.mutate({
      boardId,
      title: title.trim(),
      afterColumnId: after ? column.id : null,
    });
    onClose();
  };

  const handleSetWip = () => {
    const cur = column.wipLimit;
    const v = window.prompt('WIP-лимит (число) или пусто для снятия:', cur != null ? String(cur) : '');
    if (v === null) return;
    if (v.trim() === '') onSetWip(null);
    else {
      const n = parseInt(v.trim(), 10);
      if (Number.isFinite(n) && n > 0) onSetWip(n);
    }
    onClose();
  };

  return (
    <div className="min-w-[220px] bg-surface-light dark:bg-surface-dark border border-line-light dark:border-line-dark rounded-lg shadow-pop p-1 animate-pop">
      <MenuItem icon={Edit3} label="Переименовать" onClick={() => { onRename(); onClose(); }} />
      <div className="px-2 py-1">
        <div className="text-[10px] uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d mb-1">Тип</div>
        <div className="grid grid-cols-2 gap-1">
          {(Object.entries(COLUMN_KIND) as [ColumnKind, typeof COLUMN_KIND[ColumnKind]][]).map(([k, v]) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                onSetKind(k);
                onClose();
              }}
              className={clsx(
                'flex items-center gap-1 px-2 py-1 rounded text-[11px] hover:bg-surface2-light dark:hover:bg-surface2-dark text-left',
                column.kind === k && 'bg-surface2-light dark:bg-surface2-dark font-medium',
              )}
              style={{ color: v.accent }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.accent }} />
              <span className="text-ink-light dark:text-ink-dark">{v.label}</span>
            </button>
          ))}
        </div>
      </div>
      <MenuItem
        icon={Gauge}
        label="WIP-лимит"
        sub={column.wipLimit != null ? String(column.wipLimit) : '—'}
        onClick={handleSetWip}
      />
      <Sep />
      {canMoveLeft && <MenuItem icon={ArrowLeft} label="Переместить влево" onClick={() => { onMoveLeft(); onClose(); }} />}
      {canMoveRight && <MenuItem icon={ArrowRight} label="Переместить вправо" onClick={() => { onMoveRight(); onClose(); }} />}
      <MenuItem icon={Plus} label="Добавить колонку слева" onClick={() => handleAddNeighbor(false)} />
      <MenuItem icon={Plus} label="Добавить колонку справа" onClick={() => handleAddNeighbor(true)} />
      <Sep />
      <MenuItem
        icon={GripVertical}
        label="Превратить в группу"
        onClick={() => {
          const childTitle = window.prompt('Название первой под-колонки:', 'Под-колонка');
          if (childTitle?.trim()) {
            // Используем mutation через api.useUtils
            void utils.client.columns.convertToGroup
              .mutate({ parentColumnId: column.id, firstChildTitle: childTitle.trim() })
              .then(() => utils.boards.getFull.invalidate({ boardId }));
          }
          onClose();
        }}
      />
      <Sep />
      <MenuItem icon={Archive} label="Очистить колонку" onClick={() => { onClear(); onClose(); }} />
      <MenuItem icon={Trash2} label="Удалить колонку" danger onClick={() => { onDelete(); onClose(); }} />
    </div>
  );
}

function LaneSection({
  board,
  lane,
  columns,
  cards,
  showLaneHeader,
  gridTemplate,
  onOpenCard,
  prevLane,
  nextLane,
  activeColumnId,
  columnDragDelta,
}: {
  board: BoardRow;
  lane: LaneRow;
  columns: ColumnRow[];
  cards: CardRow[];
  showLaneHeader: boolean;
  gridTemplate: string;
  onOpenCard: (id: string) => void;
  prevLane: LaneRow | null;
  nextLane: LaneRow | null;
  activeColumnId: string | null;
  columnDragDelta: { x: number; y: number };
}) {
  const [collapsed, setCollapsed] = useState(lane.collapsed);

  return (
    <>
      {showLaneHeader && (
        <LaneHeader
          lane={lane}
          boardId={board.id}
          count={cards.length}
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
          prevLane={prevLane}
          nextLane={nextLane}
        />
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateRows: collapsed ? '0fr' : '1fr',
          transition: 'grid-template-rows 280ms cubic-bezier(0.25, 0.8, 0.25, 1)',
        }}
      >
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div
            className="grid bg-canvas-light/50 dark:bg-surface2-dark/40"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            {columns.map((col, i) => (
              <ColumnCell
                key={col.id}
                board={board}
                column={col}
                laneId={lane.id}
                cards={cards.filter((c) => c.columnId === col.id)}
                isFirst={i === 0}
                onOpenCard={onOpenCard}
                isColumnDragging={activeColumnId === col.id}
                columnDragDelta={columnDragDelta}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function LaneHeader({
  lane,
  boardId,
  count,
  collapsed,
  onToggle,
  prevLane,
  nextLane,
}: {
  lane: LaneRow;
  boardId: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  prevLane: LaneRow | null;
  nextLane: LaneRow | null;
}) {
  // useSortable для дорожек — анимация соседей при перестановке (vertical strategy
  // настраивается на SortableContext в Board). Тот же easing/duration что у колонок.
  const sortable = useSortable({
    id: lane.id,
    data: { type: 'lane', lane },
    transition: {
      duration: 180,
      easing: 'cubic-bezier(0.2, 0, 0, 1)',
    },
  });
  const utils = api.useUtils();
  const updateMutation = api.lanes.update.useMutation({
    onSuccess: () => utils.boards.getFull.invalidate({ boardId }),
  });
  const deleteMutation = api.lanes.delete.useMutation({
    onSuccess: () => utils.boards.getFull.invalidate({ boardId }),
  });
  const moveMutation = api.lanes.move.useMutation({
    onSuccess: () => utils.boards.getFull.invalidate({ boardId }),
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(lane.title);
  const [menuOpen, setMenuOpen] = useState(false);

  const submitRename = () => {
    const t = draft.trim();
    if (t && t !== lane.title) updateMutation.mutate({ id: lane.id, title: t });
    setEditing(false);
  };

  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        zIndex: sortable.isDragging ? 50 : undefined,
      }}
      className={clsx(
        'w-full flex items-center gap-1.5 px-2 h-7 hover:bg-surface2-light/40 dark:hover:bg-surface2-dark/40 transition border-t border-line-light dark:border-line-dark cursor-grab active:cursor-grabbing',
        sortable.isDragging && 'opacity-80',
      )}
      {...sortable.attributes}
      {...sortable.listeners}
    >
      <span
        className="inline-flex items-center justify-center w-3 shrink-0 text-ink-faint_l dark:text-ink-faint_d opacity-50"
        title="Перетащите дорожку"
      >
        <GripVertical size={11} />
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center w-4 h-4 text-ink-faint_l dark:text-ink-faint_d shrink-0"
        title={collapsed ? 'Развернуть' : 'Свернуть'}
      >
        <ChevronDown
          size={11}
          strokeWidth={2}
          style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)', transition: 'transform 200ms' }}
        />
      </button>
      <span
        className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
        style={{ background: lane.accent ?? '#9aa0a6' }}
      />
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={submitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitRename();
            }
            if (e.key === 'Escape') {
              setDraft(lane.title);
              setEditing(false);
            }
          }}
          className="text-[11px] font-medium uppercase tracking-wider bg-transparent border-b border-accent outline-none"
          style={{ letterSpacing: '0.05em', minWidth: 80 }}
          onMouseDown={(e) => e.stopPropagation()}
        />
      ) : (
        <button
          type="button"
          onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
          className="text-[11px] font-medium uppercase tracking-wider text-ink-muted_l dark:text-ink-muted_d text-left"
          style={{ letterSpacing: '0.05em' }}
        >
          {lane.title}
        </button>
      )}
      <span className="text-[10.5px] text-ink-faint_l dark:text-ink-faint_d tabular-nums">{count}</span>
      <div className="flex-1" />
      <LaneMenuButton
        lane={lane}
        canMoveUp={!!prevLane}
        canMoveDown={!!nextLane}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        onRename={() => setEditing(true)}
        onSetAccent={(c) => updateMutation.mutate({ id: lane.id, accent: c })}
        onMoveUp={() => {
          if (!prevLane) return;
          moveMutation.mutate({ id: lane.id, beforeLaneId: null, afterLaneId: prevLane.id });
        }}
        onMoveDown={() => {
          if (!nextLane) return;
          moveMutation.mutate({ id: lane.id, beforeLaneId: nextLane.id, afterLaneId: null });
        }}
        onDelete={() => {
          if (window.confirm(`Удалить дорожку «${lane.title}»? Карточки переедут в первую другую.`)) {
            deleteMutation.mutate({ id: lane.id });
          }
        }}
      />
    </div>
  );
}

function LaneMenuButton(props: {
  lane: LaneRow;
  canMoveUp: boolean;
  canMoveDown: boolean;
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
  onRename: () => void;
  onSetAccent: (c: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const { menuOpen, setMenuOpen, ...rest } = props;
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center w-4 h-4 rounded text-ink-faint_l dark:text-ink-faint_d hover:bg-surface3-light dark:hover:bg-surface3-dark"
        title="Меню"
      >
        <MoreHorizontal size={11} />
      </button>
      <PopoverPortal anchorRef={anchorRef} open={menuOpen} onClose={() => setMenuOpen(false)}>
        <LaneMenu {...rest} onClose={() => setMenuOpen(false)} />
      </PopoverPortal>
    </>
  );
}

function LaneMenu({
  lane,
  canMoveUp,
  canMoveDown,
  onClose,
  onRename,
  onSetAccent,
  onMoveUp,
  onMoveDown,
  onDelete,
}: {
  lane: LaneRow;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onClose: () => void;
  onRename: () => void;
  onSetAccent: (c: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="min-w-[200px] bg-surface-light dark:bg-surface-dark border border-line-light dark:border-line-dark rounded-lg shadow-pop p-1 animate-pop">
      <MenuItem icon={Edit2} label="Переименовать" onClick={() => { onRename(); onClose(); }} />
      <div className="px-2 py-1.5">
        <div className="text-[10.5px] uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d mb-1">Цвет</div>
        <div className="flex items-center gap-1">
          {LANE_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                onSetAccent(c);
                onClose();
              }}
              className={clsx(
                'w-4 h-4 rounded-full ring-1 hover:scale-110 transition',
                lane.accent === c ? 'ring-accent ring-2' : 'ring-line-light dark:ring-line-dark',
              )}
              style={{ background: c }}
              title={c}
            />
          ))}
        </div>
      </div>
      <Sep />
      {canMoveUp && <MenuItem icon={ArrowUp} label="Переместить выше" onClick={() => { onMoveUp(); onClose(); }} />}
      {canMoveDown && <MenuItem icon={ArrowDown} label="Переместить ниже" onClick={() => { onMoveDown(); onClose(); }} />}
      <Sep />
      <MenuItem icon={Trash2} label="Удалить дорожку" danger onClick={onDelete} />
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  sub,
  onClick,
  danger,
}: {
  icon: typeof Edit3;
  label: string;
  sub?: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12.5px] text-left hover:bg-surface2-light dark:hover:bg-surface2-dark',
        danger ? 'text-red-600 dark:text-red-400' : 'text-ink-light dark:text-ink-dark',
      )}
    >
      <Icon size={13} className={clsx('shrink-0', !danger && 'text-ink-muted_l dark:text-ink-muted_d')} strokeWidth={1.8} />
      <span className="flex-1 truncate">{label}</span>
      {sub && <span className="text-[11px] text-ink-faint_l dark:text-ink-faint_d truncate ml-2">{sub}</span>}
    </button>
  );
}

function Sep() {
  return <div className="my-1 border-t border-line-light dark:border-line-dark" />;
}

function ColumnCell({
  board,
  column,
  laneId,
  cards,
  isFirst,
  onOpenCard,
  isColumnDragging,
  columnDragDelta,
}: {
  board: BoardRow;
  column: ColumnRow;
  laneId: string;
  cards: CardRow[];
  isFirst: boolean;
  onOpenCard: (id: string) => void;
  // Когда колонка этой ячейки перетаскивается — применяем тот же delta что у
  // header (через transform), чтобы все cells этой колонки двигались синхронно.
  // Это решает «двигается только шапка». Без DragOverlay, так как overlay
  // ломается в scaled canvas (transform: scale(zoom)).
  isColumnDragging: boolean;
  columnDragDelta: { x: number; y: number };
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell-${column.id}-${laneId}`,
    data: { type: 'cell', columnId: column.id, laneId },
  });

  const sorted = useMemo(() => [...cards].sort((a, b) => a.sortOrder - b.sortOrder), [cards]);

  return (
    <div
      ref={setNodeRef}
      style={
        isColumnDragging
          ? {
              transform: `translate3d(${columnDragDelta.x}px, ${columnDragDelta.y}px, 0)`,
              zIndex: 99,
            }
          : undefined
      }
      className={clsx(
        'group/cell flex flex-col gap-1 px-1.5 py-1.5 min-h-[44px]',
        !isFirst && 'border-l border-line-light dark:border-line-dark',
        isOver && 'bg-accent/5 ring-1 ring-inset ring-accent/30',
      )}
    >
      {sorted.map((card) => (
        <TaskCard key={card.id} card={card} onClick={() => onOpenCard(card.id)} />
      ))}
      <QuickAdd boardId={board.id} columnId={column.id} laneId={laneId} />
    </div>
  );
}

function QuickAdd({
  boardId,
  columnId,
  laneId,
}: {
  boardId: string;
  columnId: string;
  laneId: string;
}) {
  const [composing, setComposing] = useState(false);
  const [val, setVal] = useState('');
  const utils = api.useUtils();
  const createMutation = api.cards.create.useMutation({
    onSuccess: () => {
      setVal('');
      utils.boards.getFull.invalidate({ boardId });
    },
  });

  const submit = () => {
    if (!val.trim()) {
      setComposing(false);
      return;
    }
    createMutation.mutate({ boardId, columnId, laneId, title: val.trim() });
  };

  if (composing) {
    return (
      <div className="rounded bg-surface-light dark:bg-surface3-dark shadow-board-rest">
        <textarea
          autoFocus
          rows={2}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => {
            if (!val.trim()) setComposing(false);
            else submit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
            if (e.key === 'Escape') {
              setVal('');
              setComposing(false);
            }
          }}
          placeholder="Сформулируйте задачу"
          className="w-full bg-transparent outline-none text-[12.5px] text-ink-light dark:text-ink-dark placeholder:text-ink-faint_l dark:placeholder:text-ink-faint_d px-2.5 py-1.5 leading-tight resize-none"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setComposing(true)}
      // Появляется только при hover на ячейку колонки (group/cell в ColumnCell).
      // На клавиатурном focus тоже видна — иначе не доступна с клавиатуры.
      // По просьбе пользователя без иконки +, только текст.
      className="w-full text-left px-2 py-1 rounded text-[11.5px] text-ink-faint_l dark:text-ink-faint_d hover:bg-surface2-light dark:hover:bg-surface2-dark hover:text-ink-muted_l dark:hover:text-ink-muted_d transition opacity-0 group-hover/cell:opacity-100 focus:opacity-100"
    >
      Новая задача
    </button>
  );
}
